/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useState, useEffect, useRef } from 'react';
import MultiSelectPopup from 'components/common/MultiSelectPopup';
import Attributes from 'model/stats/Attributes';
import AttributeValues from "model/stats/AttributeValues";

interface SavingThrowSelectorProps {
  values: string;
  attributes: AttributeValues;
  proficiency: number;
  alwaysShow: boolean;
  onSave: (selectedValues: string) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const SavingThrowSelector: React.FC<SavingThrowSelectorProps> = ({
  values,
  attributes,
  proficiency,
  alwaysShow,
  onSave,
  isOpen,
  setIsOpen
}) => {
  const [valuesStr, setValuesStr] = useState<string>(values);
  const [selectedAttrs, setSelectedAttrs] = useState<string[]>([]);

  const attrs = ["Strength", "Dexterity", "Constitution", "Intelligence", "Wisdom", "Charisma"];

  useEffect(() => {
    const valsArray = values?.toUpperCase().split(', ');
    const proficientAttrs: string[] = [];
    const savesArray: string[] = [];

    for (const val of valsArray || []) {
      for (const attr of attrs) {
        const attrShort = attr.toUpperCase().substring(0, 3);
        if (val.startsWith(attrShort)) {
          proficientAttrs.push(attr);
          const level = Attributes.getBonus(attributes[attr.toLowerCase() as keyof AttributeValues]) + proficiency;
          savesArray.push(attrShort + '\u00A0' + ((level > 0) ? '+' : '') + level);
        }
      }
    }
    setSelectedAttrs(proficientAttrs);
    setValuesStr(savesArray.join(", "));
  }, [proficiency, values, attributes,
      attributes.strength, attributes.dexterity, attributes.constitution,
      attributes.intelligence, attributes.wisdom, attributes.charisma]);

  const handleChange = (selectedValues: string[]) => {
    const savesArray: string[] = [];
    for (const attr of selectedValues) {
      const attrShort = attr.toUpperCase().substring(0, 3);
      const level = Attributes.getBonus(attributes[attr.toLowerCase() as keyof AttributeValues]) + proficiency;
      savesArray.push(attrShort + '\u00A0' + ((level > 0) ? '+' : '') + level);
    }
    setSelectedAttrs(selectedValues);
    setValuesStr(savesArray.join(", "));
    onSave(selectedValues.join(', '));
  };

  const valueTextRef = useRef<HTMLSpanElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (valueTextRef.current && !valueTextRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  // Check if content is empty
  if (!selectedAttrs.length && !alwaysShow && !isOpen) {
    return null;
  }

  return (
    <div className="property-line">
      <h4>Saving Throws</h4>
      <span className="multi-select-container edit-field saving-throws">
        {isOpen && (
          <MultiSelectPopup
            values={selectedAttrs.join(", ")}
            options={attrs}
            onChange={handleChange}
            onClickOutside={handleClickOutside}
          />
        )}
        <span onMouseDown={() => setIsOpen(true)} ref={valueTextRef}>
          <span>
            {valuesStr || '\u00A0 -- \u00A0' /* spaces to give a click target */}
          </span>
        </span>
      </span>
    </div>
  );
};

export default SavingThrowSelector;
